import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import { bindDialog, type PopupState } from "material-ui-popup-state/hooks";
import { useSnackbar } from "notistack";
import { FormattedMessage } from "react-intl";
import * as yup from "yup";

import { useSendasupportrequestWIP } from "api/generated/v2/support";
import FormikCheckbox from "components/forms/mui/FormikCheckbox";
import FormikTextField from "components/forms/mui/FormikTextField";
import { SnackbarActionIcon } from "components/providers/ReactQueryProvider";
import { useUserStore } from "components/stores/UserStore";

const schema = yup.object().shape({
  email: yup.string().email(),
  description: yup.string().required().max(1000),
  consentToFurtherContact: yup.boolean().required(),
});

type FormValues = yup.InferType<typeof schema>;

function SuggestionsDialog({ ...popupState }: PopupState) {
  const { user } = useUserStore();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { mutateAsync: sendFeedback } = useSendasupportrequestWIP({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar(
          <FormattedMessage defaultMessage="An email confirmation is on its way to you shortly. If you don't see it in your inbox, please take a moment to check your spam folder, just in case." />,
          {
            variant: "success",
            action: (key) => (
              <SnackbarActionIcon
                action={() => {
                  closeSnackbar(key);
                }}
              />
            ),
            key: "feddback-suggestion-success",
            autoHideDuration: 10000,
          },
        );
      },
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      email: user?.UserV2?.user?.email || "",
      description: "",
      consentToFurtherContact: false,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await sendFeedback({
        data: {
          MayContactMe: values.consentToFurtherContact ? "yes" : "no",
          Text: values.description,
          Type: "idea",
          Url: btoa(window.location.href),
        },
      });
      formik.resetForm();
      popupState.close();
    },
    validationSchema: schema,
  });

  return (
    <Dialog {...bindDialog(popupState)}>
      <FormikProvider value={formik}>
        <Form noValidate>
          <DialogTitle>
            <FormattedMessage defaultMessage="Share your ideas!" />
          </DialogTitle>
          <DialogContent>
            <p>
              <FormattedMessage defaultMessage="We value your input and want to make our product better for you. If you have any <b>ideas</b>, <b>suggestions</b>, or <b>feature requests</b>, please share them here." />
            </p>
            <p>
              <FormattedMessage defaultMessage="Your feedback helps us enhance our offerings and create a more delightful experience. Thank you for being part of our journey!" />
            </p>
            <FormikTextField
              name="email"
              label={<FormattedMessage defaultMessage="Email" />}
            />
            <FormikTextField
              name="description"
              label={
                <FormattedMessage defaultMessage="Describe your question" />
              }
              multiline
              minRows={4}
              helperText={
                <FormattedMessage
                  defaultMessage={"{charactersLeft}/1000 characters remaining"}
                  values={{
                    charactersLeft: 1000 - formik.values.description.length,
                  }}
                />
              }
            />
            <FormikCheckbox
              name="consentToFurtherContact"
              label={
                <FormattedMessage defaultMessage="Plug AS may contact me for further details." />
              }
            />
            <Typography variant="body2">
              <FormattedMessage defaultMessage="By submitting this form, you agree that your email address and the URL of the page are attached to the ticket." />
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button type="button" onClick={popupState.close}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Submit request
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}

export default SuggestionsDialog;
